export const COPY = {
  nav: {
    brand: "GUARD DOG",
    links: [
      { label: "Services", to: "/services" },
      { label: "Why Us", to: "/why-guard-dogs" },
      { label: "About", to: "/about" },
      { label: "Contact", to: "/contact" },
    ],
  },
  hero: {
    tag: "NIL Management for Collegiate Athletes",
    headline: "Protect the Bag.",
    subheadline:
      "Your talent got you here. We make sure the money stays. Guard Dog helps collegiate athletes manage NIL deals, build real wealth, and stay protected from day one.",
    cta: "Let's Build",
    ctaSecondary: "Schedule a Call",
  },
  mission: "We help athletes earn, manage, and maximize their NIL opportunities while developing into professionals on and off the field.",
  services: {
    tag: "Services",
    headline: "The Deals That Define Careers.",
    intro:
      "Your career has three chapters now: high school, college, and the league. Every contract, every negotiation, every transfer portal window, and every move into the pros. Guard Dog represents athletes through all of it.",
    items: [
      {
        title: "High School NIL Representation",
        slug: "high-school-nil",
        num: "01",
        body: "High school athletes are cashing NIL checks before they can legally sign a lease. We make sure the first dollar earned builds a foundation that lasts.",
        detail: {
          tagline: "The youngest generation of athletes ever to earn. We protect them first.",
          paragraphs: [
            "High school NIL is here, and most athletes and families are walking into it without representation, without structure, and without anyone watching the money. Guard Dog steps in before the first deal gets signed.",
            "We represent high school athletes across contract review, brand alignment, family education, and long-term planning. From local partnerships to six-figure national deals, we make sure the terms protect the athlete and the money gets handled right.",
            "The habits that start here compound for life. A player who learns to read a contract at sixteen does not get taken advantage of at twenty-two. A family that understands tax exposure in high school does not get blindsided in college. That is the point.",
          ],
          features: [
            "NIL contract review and negotiation",
            "Family financial education and planning",
            "Brand alignment and long-term image strategy",
            "Tax setup before the first deal closes",
          ],
        },
      },
      {
        title: "College NIL Representation",
        slug: "college-nil",
        num: "02",
        body: "Six-figure deals are landing in dorm rooms with nobody reading the fine print. We negotiate, protect, and manage the full NIL life of a college athlete, from first campus deal to the transfer portal window.",
        detail: {
          tagline: "Every deal you sign in college sets the number on every deal after it.",
          paragraphs: [
            "College is where NIL money gets serious, and where most athletes end up accepting the first offer they get. Guard Dog steps in as your representation so every contract you touch reflects what you are actually worth.",
            "We negotiate with collectives and brands, pressure-test promises, and run the transfer portal window when the time comes. When your program changes, your value changes, and we make sure the new package matches it.",
            "And once the paper is signed, the work continues. Multi-state tax exposure, long-term wealth strategy, and compliance all get handled so your focus stays on the game.",
          ],
          features: [
            "NIL contract review and negotiation",
            "Collective and brand deal management",
            "Transfer portal package evaluation and negotiation",
            "Multi-state tax and compliance across programs",
          ],
        },
      },
      {
        title: "NFL Representation",
        slug: "nfl-representation",
        num: "03",
        body: "When the league calls, the team that protected you in college does not disappear. Guard Dog works alongside trusted NFL agents to handle on-field contracts while we manage the off-field life that makes or breaks pros.",
        detail: {
          tagline: "Most pros lose it within five years. Our guys do not.",
          paragraphs: [
            "Going pro is the moment most athletes get separated from the people who actually had their back. New agents, new advisors, new everyone. Guard Dog refuses to let that happen. We work with a vetted network of NFL agents who handle the on-field contract, while Guard Dog continues to run everything around it.",
            "That means your wealth strategy, tax planning, brand, and lifestyle do not start over the day you get drafted. The infrastructure we built in high school and college scales with you into the league, and the NFL agent we partner you with operates inside that system, not around it.",
            "The result is one team, end to end. The agent fights for the contract. We fight for the rest of your life.",
          ],
          features: [
            "Vetted NFL agent partnerships for on-field contracts",
            "Continuity of wealth, tax, and brand strategy from college to pro",
            "Endorsement and marketing deal oversight",
            "Long-term planning for life after the league",
          ],
        },
      },
    ],
  },
  why: {
    tag: "Why Guard Dog",
    headline: "Built for Athletes. Not Banks.",
    body: "NIL changed the game overnight. College athletes are signing six-figure deals with no financial infrastructure around them. No tax prep. No investment strategy. No one watching the money. Guard Dog was built to fill that gap, because the habits you build now determine whether you are set for life or starting over.",
    stats: [
      { number: "$1.17B", label: "in NIL deals signed by college athletes since 2021" },
      { number: "2%", label: "of college athletes have any form of professional financial guidance" },
      { number: "500K+", label: "college athletes now eligible for NIL income with zero financial training" },
    ],
    footnote: "For context: 78% of NFL and 60% of NBA players face financial hardship within 5 years of retirement. The pattern starts in college.",
    pillarsTag: "What We Do",
    pillarsHeadline: "Full Coverage. No Blind Spots.",
    pillars: [
      {
        title: "Athlete Development",
        slug: "athlete-development",
        num: "01",
        body: "Training, preparation, and performance. We build the foundation that keeps athletes competitive on the field and ready for what comes next.",
        detail: {
          tagline: "Built for athletes who want more than just a highlight reel.",
          paragraphs: [
            "Guard Dog partners with athletes to develop skills that go beyond the game. From training programs to mentorship pipelines, we connect you with the resources that keep you performing at your peak.",
            "Our Athlete Development program works hand in hand with TGQ Performance, feeding athletes from the recruiting pipeline directly into Guard Dog's full suite of NIL management services.",
            "Whether you are training for the combine, preparing for your first NIL deal, or building your personal brand, Athlete Development is where it starts.",
          ],
          features: [
            "Performance training partnerships",
            "Recruiting pipeline through TGQ Performance",
            "Personal brand development",
            "Mentorship and career readiness",
          ],
        },
      },
      {
        title: "Wealth Strategy",
        slug: "wealth-strategy",
        num: "02",
        body: "Smart, diversified portfolios tailored to your timeline and risk. We grow your money strategically while you focus on your game.",
        detail: {
          tagline: "Your career has a clock. Your wealth does not have to.",
          paragraphs: [
            "NIL money comes fast, but it does not last forever. Wealth Strategy is about building a financial plan that outlives your playing career. We design portfolios around your goals, risk tolerance, and timeline.",
            "From your first NIL check to long-term investments in real estate, trusts, and diversified assets, Guard Dog builds a strategy that grows with you.",
            "We do not gamble with your money. Every decision is backed by data, experience, and a long-term vision for your financial future.",
          ],
          features: [
            "Personalized investment portfolios",
            "Real estate and alternative assets",
            "Trust and estate planning",
            "Long-term wealth building beyond athletics",
          ],
        },
      },
      {
        title: "Tax & Compliance",
        slug: "tax-compliance",
        num: "03",
        body: "Multi-state NIL income gets complicated fast. We handle the tax planning, quarterly filings, and deductions so you never get blindsided by the IRS.",
        detail: {
          tagline: "NIL income is complicated. Your tax strategy should not be.",
          paragraphs: [
            "Earning NIL income across multiple states creates a tax situation most college athletes are not prepared for. Guard Dog handles the complexity so you can focus on playing.",
            "Our tax and compliance team manages quarterly filings, deduction strategies, and multi-state compliance. We make sure every dollar is accounted for and every filing is on time.",
            "From your first NIL payment to year-end reporting, we keep you compliant and protected from surprise tax bills.",
          ],
          features: [
            "Multi-state NIL tax planning",
            "Quarterly filing management",
            "Deduction optimization",
            "Full IRS compliance and audit readiness",
          ],
        },
      },
      {
        title: "Lifestyle & Experiences",
        slug: "lifestyle-experiences",
        num: "04",
        body: "The luxury and brand layer. Experiences, lifestyle management, and identity building for athletes who want to live as big as they play.",
        detail: {
          tagline: "Your brand is bigger than your stats.",
          paragraphs: [
            "Guard Dog goes beyond the numbers. Lifestyle & Experiences is about building the life and identity that matches your talent. We help athletes curate their personal brand, access exclusive experiences, and manage the lifestyle side of success.",
            "From brand partnerships and social media strategy to travel and event access, this pillar is designed for athletes who want to build something that lasts beyond the field.",
            "Your image, your identity, your legacy. We help you build all three.",
          ],
          features: [
            "Personal brand and image management",
            "Exclusive lifestyle experiences",
            "Social media and content strategy",
            "Brand partnership coordination",
          ],
        },
      },
      {
        title: "Branding & Marketing",
        slug: "branding-marketing",
        num: "05",
        body: "The business side of being you. We build the brand, source the deals, and run the campaigns so every endorsement raises your number for the next one.",
        detail: {
          tagline: "Your name is the product. We make sure it sells like one.",
          paragraphs: [
            "Lifestyle is what you live. Branding & Marketing is what you sell. This pillar handles the commercial side of an athlete's name, image, and likeness. Media kits, brand positioning, deal sourcing, and the campaigns that turn audience into income.",
            "We pitch you to the right brands, negotiate placements that fit your identity, and run integrated campaigns across paid, organic, and earned channels. Every deal is built to compound, so the next sponsor pays more than the last one.",
            "The athletes who win long term are not the ones with the most followers. They are the ones with the cleanest brand, the sharpest pitch, and the team that knows how to move it.",
          ],
          features: [
            "Brand positioning and media kit development",
            "Endorsement and sponsorship deal sourcing",
            "Integrated marketing campaigns across paid, organic, and earned",
            "Performance tracking and rate-card growth over time",
          ],
        },
      },
    ],
  },
  about: {
    tag: "The Team",
    headline: "People Who Get It.",
    body: "Guard Dog was built by people who understand the game from every angle. From athlete development and financial strategy to tax compliance and lifestyle management, our team brings the experience and focus that college athletes need to protect what they earn.",
    founder: {
      name: 'Quentin "Q" Jones',
      role: "Founder, Director of Athlete Development",
      bio: [
        "Quentin Jones built Guard Dog Management around one mission. Athletes shouldn't just make money. They should keep it, grow it, and use it the right way. He's assembled a team of specialists across wealth, tax, and lifestyle to make sure every dollar earned gets protected.",
        "With a background as a high-level defensive back and coaching experience at the Power 4 and NFL level, Q understands exactly what athletes face: recruitment, NIL deals, preparing for the next level.",
        "He has worked inside elite football environments, including Georgia Tech and the NFL through the Bill Walsh Fellowship.",
      ],
      quote: "Most athletes don't lose because they can't make money. They lose because they don't know how to manage it.",
    },
    differentiators: {
      tag: "The Difference",
      headline: "Q Doesn't Just Advise Athletes. He Develops Them.",
      items: [
        { num: "01", title: "On the Field", body: "Training mindset, development, preparation." },
        { num: "02", title: "In Decision-Making", body: "Helping athletes and families understand opportunities." },
        { num: "03", title: "Beyond the Game", body: "Building structure around money, brand, and life." },
      ],
    },
    closing: {
      headline: "This Isn't Just NIL. This Is Protection.",
      body: "Most athletes are focused on getting paid. We're focused on what happens after. Guard Dog Management is built for athletes and families who want to move smart and build something that lasts.",
      button: "Let's Build",
    },
    team: [
      { name: "Q", role: "Founder / Director of Athlete Development" },
      { name: "Aeshia", role: "Director of Wealth Strategy" },
      { name: "Angie", role: "Director of Tax & Compliance" },
      { name: "Kimberly", role: "Director of Lifestyle & Experiences" },
    ],
  },
  cta: {
    headline: "Your Money. Your Future. Our Watch.",
    body: "Ready to stop leaving money on the table? Let's talk.",
    button: "Let's Build",
  },
  contact: {
    tag: "Get In Touch",
    headline: "Let's Build.",
    intro: "Whether you just signed your first NIL deal or you are looking for long-term wealth strategy, we are here. No pressure, no pitch. Just a conversation about protecting what is yours.",
    email: "info@guarddogmgmt.com",
    location: "Atlanta, GA",
    serviceOptions: [
      "High School NIL Representation",
      "College NIL Representation",
      "NFL Representation",
      "Athlete Development",
      "Wealth Strategy",
      "Tax & Compliance",
      "Lifestyle & Experiences",
      "Branding & Marketing",
      "All of the Above",
    ],
  },
  footer: {
    copy: "\u00a9 2026 Guard Dog Management Inc. All rights reserved.",
  },
};
